<script setup lang="ts">
import { QuillEditor } from '@vueup/vue-quill'
import { useField, useForm } from 'vee-validate'
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import * as yup from 'yup'
import { type CreateServicePayload, createServiceAction } from '@/api/functions/services/create-service.action.ts'
import { uploadServiceLogoAction } from '@/api/functions/services/upload-service-logo.action.ts'
import { useMutationApi } from '@/composables/useApi.ts'
import { useGeneralSettings } from '@/composables/useGeneralSettings.ts'
import {
  useOrderTypes,
  useServiceCategories,
  useServiceProviders,
  useServiceRegions,
  useServiceTypes,
} from '@/composables/useServices.ts'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils.ts'
import CreateProviderModal from '../settings/service-providers/partials/CreateProviderModal.vue'
import '@vueup/vue-quill/dist/vue-quill.snow.css'

const router = useRouter()
const toast = useToast()
// const serviceLogo = ref<File | null>(null)
// const serviceLogoUrl = ref<string | null>(null)
const isCreateProviderModalOpen = ref(false)
const planStatus = ref<'ACTIVE' | 'INACTIVE'>('ACTIVE')
const planSortOrder = ref<number>(0)
const planFormKey = ref(0)
// const canUploadLogo = computed(() => Boolean(slug.value))

const toolbarOptions = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ align: [] }],
  ['blockquote', 'code-block'],
  ['link'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ direction: 'rtl' }],
  [{ color: [] }, { background: [] }],
  ['clean'],
]

const planStatusOptions = [
  {
    label: 'فعال',
    value: 'ACTIVE',
  },
  {
    label: 'غیرفعال',
    value: 'INACTIVE',
  },
]

type ServiceStatus = 'ACTIVE' | 'INACTIVE'

interface ServicePlanRegion {
  region: string
  amount: number | null
  currency: string
}

interface ServicePlan {
  name: string
  description: string
  duration_days: number | null
  sort_order: number
  has_region: boolean
  amount: number | null
  currency: string
  regions: ServicePlanRegion[]
  is_active: boolean
}

// interface CustomField {
//     name: string
//     label: string
//     type: string
//     placeholder: string
//     is_required: boolean
//     default_value: string | null
//     sort_order: number
// }

interface ServiceFormValues {
  name: string
  slug: string
  providerName: string
  providerWebsite: string
  category: string[]
  serviceType: string
  shortDescription: string
  description: string
  status: ServiceStatus
  isFeatured: boolean
  featuredSortOrder: number
  orderTypes: string[]
  sortOrder: number
}

const schema = yup.object({
  name: yup
    .string()
    .required('نام سرویس الزامی است')
    .min(2, 'نام سرویس باید حداقل 2 کاراکتر باشد')
    .max(100, 'نام سرویس نمی‌تواند بیشتر از 100 کاراکتر باشد'),

  slug: yup
    .string()
    .required('اسلاگ الزامی است')
    .matches(/^[a-z_]+$/, 'اسلاگ فقط می‌تواند شامل حروف انگلیسی کوچک و _ باشد')
    .min(2, 'اسلاگ باید حداقل 2 کاراکتر باشد')
    .max(100, 'اسلاگ نمی‌تواند بیشتر از 100 کاراکتر باشد'),

  providerName: yup.string().required('نام ارائه‌دهنده الزامی است'),

  category: yup.array().of(yup.string()).min(1, 'حداقل یک دسته‌بندی را انتخاب کنید').required('دسته‌بندی الزامی است'),

  serviceType: yup.string().required('نوع سرویس الزامی است'),

  orderTypes: yup.array().of(yup.string()).min(1, 'حداقل یک نوع سفارش را انتخاب کنید').required('نوع سفارش الزامی است'),

  shortDescription: yup.string().max(500, 'توضیحات کوتاه نمی‌تواند بیشتر از 500 کاراکتر باشد'),

  description: yup.string().max(2000, 'توضیحات نمی‌تواند بیشتر از 2000 کاراکتر باشد'),

  status: yup.string().required('وضعیت الزامی است'),

  isFeatured: yup.boolean(),
  featuredSortOrder: yup
    .number()
    .typeError('ترتیب نمایش سرویس ویژه باید عدد باشد')
    .min(0, 'ترتیب نمایش نمی‌تواند منفی باشد')
    .when('isFeatured', {
      is: true,
      then: (schema) => schema.required('ترتیب نمایش سرویس ویژه الزامی است'),
    }),

  sortOrder: yup.number().typeError('ترتیب نمایش باید عدد باشد').min(0, 'ترتیب نمایش نمی‌تواند منفی باشد'),
})

const { handleSubmit, errors } = useForm<ServiceFormValues>({
  validationSchema: schema,
  initialValues: {
    name: '',
    slug: '',
    providerName: '',
    category: [],
    serviceType: '',
    orderTypes: [],
    shortDescription: '',
    description: '',
    status: 'ACTIVE',
    isFeatured: true,
    featuredSortOrder: 0,
    sortOrder: 0,
  },
})

const { value: name } = useField<string>('name')
const { value: slug } = useField<string>('slug')
const { value: providerName } = useField<string>('providerName')
const { value: category } = useField<string[]>('category')
const { value: serviceType } = useField<string>('serviceType')
const { value: shortDescription } = useField<string>('shortDescription')
const { value: orderTypes } = useField<string[]>('orderTypes')
const { value: description } = useField<string>('description')
const { value: status } = useField<ServiceStatus>('status')
const { value: isFeatured } = useField<boolean>('isFeatured')
const { value: featuredSortOrder } = useField<number>('featuredSortOrder')
const { value: sortOrder } = useField<number>('sortOrder')

const handleSlugInput = (event: Event) => {
  const input = event.target as HTMLInputElement

  slug.value = input.value
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z_]/g, '')
}

const { categoryOptions } = useServiceCategories()

const { serviceTypeOptions } = useServiceTypes()

const { orderTypeOptions } = useOrderTypes()

const { providerOptions, refetch: refetchProviders } = useServiceProviders()

const { currencies } = useGeneralSettings()
const { regionOptions } = useServiceRegions()

const statusOptions = [
  {
    label: 'فعال',
    value: 'ACTIVE',
  },
  {
    label: 'غیرفعال',
    value: 'INACTIVE',
  },
]

//  Plans
const plans = ref<ServicePlan[]>([])

const planName = ref('')
const planDescription = ref('')
const planDuration = ref<number | null>(null)
const hasRegion = ref(false)
const selectedRegions = ref<string[]>([])
const planAmount = ref<number | null>(null)
const planCurrency = ref('')
const planRegions = ref<ServicePlanRegion[]>([])

const handleRegionsChange = (regions: string[]) => {
  planRegions.value = regions.map((region) => {
    const existingRegion = planRegions.value.find((item) => item.region === region)

    return (
      existingRegion || {
        region,
        amount: null,
        currency: '',
      }
    )
  })
}

const addPlan = () => {
  if (!planName.value.trim()) {
    toast.error('نام پلن را وارد کنید', 'خطا')
    return
  }

  if (hasRegion.value) {
    if (!selectedRegions.value.length) {
      toast.error('حداقل یک ریجن را انتخاب کنید', 'خطا')
      return
    }

    plans.value.push({
      name: planName.value.trim(),
      description: planDescription.value.trim(),
      is_active: planStatus.value === 'ACTIVE',
      duration_days: planDuration.value,
      sort_order: Number(planSortOrder.value),
      has_region: true,
      amount: null,
      currency: '',
      regions: planRegions.value.map((region) => ({
        ...region,
      })),
    })
  } else {
    plans.value.push({
      name: planName.value.trim(),
      description: planDescription.value.trim(),
      is_active: planStatus.value === 'ACTIVE',
      duration_days: planDuration.value,
      sort_order: Number(planSortOrder.value),
      has_region: false,
      amount: planAmount.value,
      currency: planCurrency.value,
      regions: [],
    })
  }

  planName.value = ''
  planDescription.value = ''
  planDuration.value = null
  hasRegion.value = false
  selectedRegions.value = []
  planAmount.value = null
  planCurrency.value = ''
  planRegions.value = []
  planStatus.value = 'ACTIVE'
  planSortOrder.value = plans.value.length
}

const removePlan = (index: number) => {
  plans.value.splice(index, 1)

  plans.value.forEach((plan, index) => {
    plan.sort_order = index
  })
}

//  Custom Fields
// const customFields = ref<CustomField[]>([
//     {
//         name: 'account_email',
//         label: 'ایمیل حساب',
//         type: 'email',
//         placeholder: 'example@email.com',
//         is_required: false,
//         default_value: null,
//         sort_order: 0,
//     },
//     {
//         name: 'account_username',
//         label: 'نام کاربری',
//         type: 'text',
//         placeholder: 'نام کاربری حساب',
//         is_required: false,
//         default_value: null,
//         sort_order: 1,
//     },
// ])

// const customFieldName = ref('')
// const customFieldLabel = ref('')
// const customFieldType = ref('')
// const customFieldPlaceholder = ref('')
// const customFieldRequired = ref(false)

// const customFieldTypeOptions = [
//     {
//         label: 'متن',
//         value: 'text',
//     },
//     {
//         label: 'ایمیل',
//         value: 'email',
//     },
//     {
//         label: 'عدد',
//         value: 'number',
//     },
//     {
//         label: 'رمز عبور',
//         value: 'password',
//     },
// ]

// const addCustomField = () => {
//     if (!customFieldName.value.trim()) {
//         toast.error(
//             'نام فیلد را وارد کنید',
//             'خطا'
//         )
//         return
//     }

//     if (!customFieldLabel.value.trim()) {
//         toast.error(
//             'عنوان فیلد را وارد کنید',
//             'خطا'
//         )
//         return
//     }

//     if (!customFieldType.value) {
//         toast.error(
//             'نوع فیلد را انتخاب کنید',
//             'خطا'
//         )
//         return
//     }

//     customFields.value.push({
//         name: customFieldName.value.trim(),
//         label: customFieldLabel.value.trim(),
//         type: customFieldType.value,
//         placeholder:
//             customFieldPlaceholder.value.trim(),
//         is_required:
//             customFieldRequired.value,
//         default_value: null,
//         sort_order: customFields.value.length,
//     })

//     customFieldName.value = ''
//     customFieldLabel.value = ''
//     customFieldType.value = ''
//     customFieldPlaceholder.value = ''
//     customFieldRequired.value = false
// }

// const removeCustomField = (index: number) => {
//     customFields.value.splice(index, 1)

//     customFields.value.forEach(
//         (field, index) => {
//             field.sort_order = index
//         }
//     )
// }

const createServiceMutation = useMutationApi({
  mutationFn: (payload: CreateServicePayload) => createServiceAction(payload),

  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const isSubmitting = computed(() => createServiceMutation.isPending.value)

// Submit
const onSubmit = handleSubmit(async (values) => {
  const payload: CreateServicePayload = {
    category_ids: values.category,
    provider_id: values.providerName,
    name: values.name,
    slug: values.slug,
    service_type_id: values.serviceType,
    short_description: values.shortDescription,
    description: values.description,
    // logo_url: serviceLogoUrl.value,

    is_featured: values.isFeatured,
    is_active: values.status === 'ACTIVE',
    sort_order: Number(values.sortOrder),
    popular_sort_order: values.isFeatured ? Number(values.featuredSortOrder ?? 0) : null,

    plans: plans.value.map((plan) => {
      const regions = plan.has_region
        ? plan.regions
        : [
            {
              region: '',
              amount: plan.amount,
              currency: plan.currency,
            },
          ]

      return {
        name: plan.name,
        description: plan.description || null,
        duration_days: plan.duration_days,
        sort_order: plan.sort_order,
        is_active: plan.is_active,

        prices: regions.map((region) => ({
          pricing_type: 'fixed',
          region_id: region.region ? region.region : null,
          currency_id: region.currency,
          amount: String(region.amount ?? 0),
        })),
      }
    }),

    order_type_ids: values.orderTypes,
  }

  try {
    await createServiceMutation.mutateAsync(payload)

    toast.success('سرویس با موفقیت ایجاد شد')
    router.push('/services')
  } catch {}
})

const onCancel = () => {
  router.push('/services')
}

// const checkLogoUpload = () => {
//     if (!slug.value) {
//         toast.error(
//             'برای آپلود لوگو ابتدا اسلاگ سرویس را وارد کنید',
//             'خطا'
//         )

//         return false
//     }

//     return true
// }

// const uploadLogoMutation = useMutationApi({
//     mutationFn: ({
//         serviceSlug,
//         logo,
//     }: {
//         serviceSlug: string
//         logo: File
//     }) =>
//         uploadServiceLogoAction(
//             serviceSlug,
//             logo,
//         ),

//     onError: (error: Error) => {
//         toast.error(getErrorMessage(error), 'خطا')
//     },
// })

// const uploadServiceLogo = async (file: File) => {
//     if (!slug.value) {
//         return
//     }

//     try {
//         const response = await uploadLogoMutation.mutateAsync({
//             serviceSlug: slug.value,
//             logo: file,
//         })

//         serviceLogo.value = file
//         serviceLogoUrl.value = response.data.logo_url

//         toast.success('لوگوی سرویس با موفقیت آپلود شد')
//     } catch {
//         serviceLogo.value = null
//         serviceLogoUrl.value = null
//     }
// }
</script>

<template>
  <div class="mb-12 w-full min-w-0">
    <CreateProviderModal v-model="isCreateProviderModalOpen" @confirm="refetchProviders" />

    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
      <router-link to="/services"
        class="w-fit flex items-center h-10 rounded-lg text-[#2d5396] px-5 hover:bg-[#D5E2F1] text-[13px]">
        <img src="/svg/back-icon.svg" class="me-1" width="22" />
        <span>بازگشت</span>
      </router-link>

      <div class="flex justify-end gap-4 mt-2">
        <Button form="create-service-form" :loading="isSubmitting" :disabled="isSubmitting" type="submit"
          label="ایجاد سرویس" :isAdmin="true" class="px-10 h-12 rounded-xl" />
      </div>
    </div>

    <h2 class="text-[22px] text-[#1b325a] mb-8">
      افزودن سرویس جدید
    </h2>

    <form id="create-service-form" @submit.prevent="onSubmit" class="flex flex-col gap-8 w-full min-w-0">
      <!-- Main Information -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 w-full min-w-0">

        <!-- Basic Information -->
        <div class="w-full min-w-0 bg-white border border-[#d8d8d8] rounded-xl p-5 lg:p-8 lg:col-span-2">
          <h3 class="text-[17px] text-[#1b325a] font-medium mb-7">
            اطلاعات سرویس
          </h3>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 pb-2 min-w-0">

            <div class="min-w-0">
              <Input v-model="name" name="name" label="نام سرویس" type="text" :error="errors.name" />
            </div>

            <div class="min-w-0">
              <Input v-model="slug" name="slug" label="اسلاگ" type="text" @input="handleSlugInput"
                :error="errors.slug" />
            </div>

            <div class="min-w-0">
              <MultiSelect v-model="category" variant="checkbox" name="category" label="دسته‌بندی"
                :options="categoryOptions" :error="errors.category" />
            </div>

            <div class="min-w-0">
              <Select v-model="serviceType" name="serviceType" label="نوع سرویس" :options="serviceTypeOptions"
                :error="errors.serviceType" />
            </div>

            <div class="min-w-0">
              <MultiSelect v-model="orderTypes" variant="checkbox" name="orderTypes" label="نوع سفارش"
                :options="orderTypeOptions" :error="errors.orderTypes" />
            </div>

            <div class="min-w-0">
              <Input v-model="sortOrder" name="sortOrder" label="ترتیب نمایش" type="number" :error="errors.sortOrder" />
            </div>

            <div class="sm:col-span-2 min-w-0">
              <Input v-model="shortDescription" name="shortDescription" label="توضیحات کوتاه" type="text"
                :error="errors.shortDescription" />
            </div>

            <div class="sm:col-span-2 min-w-0">
              <label class="block text-sm text-[#1b325a] mb-3">
                توضیحات
              </label>

              <div class="border border-[#d5ddea] rounded-xl">
                <QuillEditor v-model:content="description" theme="snow" :toolbar="toolbarOptions" content-type="html"
                  placeholder="توضیحات سرویس را وارد کنید" class="bg-white min-h-[100px] rounded-b-xl text-right" />
              </div>


              <p v-if="errors.description" class="text-xs text-[#ed145b] mt-2">
                {{ errors.description }}
              </p>
            </div>

          </div>
        </div>

        <!-- Sidebar -->
        <div class="w-full min-w-0 flex flex-col gap-6">

          <!-- Provider -->
          <div class="w-full min-w-0 bg-white border border-[#d8d8d8] rounded-xl p-5 lg:p-8">
            <h3 class="text-[17px] text-[#1b325a] font-medium mb-7">
              ارائه‌دهنده سرویس
            </h3>

            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6 min-w-0">
              <div class="min-w-0">
                <Select v-model="providerName" name="providerName" label="ارائه‌دهنده" :options="providerOptions"
                  :error="errors.providerName" />
              </div>

              <div class="flex justify-start sm:justify-end lg:justify-start items-start min-w-0">
                <Button @click="isCreateProviderModalOpen = true" type="button" class="w-fit max-w-full"
                  :isAdmin="true">
                  افزودن ارائه دهنده جدید +
                </Button>
              </div>
            </div>
          </div>

          <!-- Status -->
          <div class="w-full min-w-0 bg-white border border-[#d8d8d8] rounded-xl p-5 lg:p-8">
            <h3 class="text-[17px] text-[#1b325a] font-medium mb-7">
              وضعیت سرویس
            </h3>

            <div class="grid grid-cols-1 gap-6 min-w-0">
              <div class="min-w-0">
                <Select v-model="status" name="status" label="وضعیت" :options="statusOptions" :error="errors.status" />
              </div>

              <div class="flex flex-col gap-4 px-2 min-w-0">
                <div class="flex-1 min-w-0">
                  <Checkbox v-model="isFeatured" name="isFeatured" label="نمایش به عنوان سرویس ویژه" color="#1b325a"
                    labelColor="#1b325a" />
                </div>

                <div v-if="isFeatured" class="w-full min-w-0">
                  <Input v-model="featuredSortOrder" class="h-12!" name="featuredSortOrder"
                    label="ترتیب نمایش در سرویس‌های ویژه" type="number" :error="errors.featuredSortOrder" />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Plans -->
      <div class="w-full min-w-0 bg-white border border-[#d8d8d8] rounded-xl p-5 lg:p-8">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-7">
          <h3 class="text-[17px] text-[#1b325a] font-medium">
            پلن‌های سرویس
          </h3>

          <span class="text-xs text-[#71809b]">
            {{ plans.length }} پلن
          </span>
        </div>

        <!-- Add Plan -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 pb-7 border-b border-[#eaeef5] min-w-0">
          <div class="min-w-0">
            <Input name="planName" v-model="planName" label="نام پلن" type="text" />
          </div>

          <div class="min-w-0">
            <Input name="planDuration" v-model="planDuration" label="مدت اعتبار (روز)" type="number" />
          </div>

          <div class="min-w-0">
            <Select v-model="planStatus" name="planStatus" label="وضعیت پلن" :options="planStatusOptions" />
          </div>

          <div class="min-w-0">
            <Input name="planSortOrder" v-model="planSortOrder" label="ترتیب نمایش پلن" type="number" />
          </div>

          <div class="sm:col-span-2 min-w-0">
            <Textarea name="planDescription" v-model="planDescription" label="توضیحات پلن" type="text" />
          </div>

          <div class="sm:col-span-2 mt-8 sm:mt-12 mb-2 min-w-0">
            <Checkbox v-model="hasRegion" name="hasRegion" label="این پلن دارای ریجن است" color="#1b325a"
              labelColor="#1b325a" />
          </div>

          <!-- Without Region -->
          <template v-if="!hasRegion">
            <div class="min-w-0">
              <Input v-model="planAmount" name="planAmount" label="مبلغ" type="text" inputmode="decimal" />
            </div>

            <div class="min-w-0">
              <Select v-model="planCurrency" name="planCurrency" label="ارز" :options="currencies" />
            </div>
          </template>

          <!-- With Region -->
          <template v-else>
            <div class="sm:col-span-2 min-w-0">
              <MultiSelect v-model="selectedRegions" variant="checkbox" name="selectedRegions" label="ریجن"
                :options="regionOptions" @update:modelValue="handleRegionsChange" />
            </div>

            <div v-for="region in planRegions" :key="region.region"
              class="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6 p-4 rounded-xl bg-[#f7f9fc] border border-[#e5eaf2] min-w-0">
              <div class="min-w-0">
                <Input v-model="region.amount" :name="`region-${region.region}-amount`"
                  :label="`مبلغ ریجن ${regionOptions.find(item => item.value === region.region)?.label}`" type="text"
                  inputmode="decimal" />
              </div>

              <div class="min-w-0">
                <Select v-model="region.currency" :name="`region-${region.region}-currency`"
                  :label="`ارز ریجن ${regionOptions.find(item => item.value === region.region)?.label}`"
                  :options="currencies" />
              </div>
            </div>
          </template>

          <div class="sm:col-span-2 flex justify-end mt-6 sm:mt-10 min-w-0">
            <Button type="button" :isAdmin="true" class="px-6 rounded-lg h-10 py-0 text-[13px]" @click="addPlan">
              افزودن پلن
            </Button>
          </div>
        </div>

        <!-- Plans List -->
        <div v-if="plans.length" class="flex flex-col gap-3 mt-6 min-w-0">
          <div v-for="(plan, index) in plans" :key="`${plan.name}-${index}`"
            class="flex items-start sm:items-center justify-between gap-4 p-4 rounded-xl bg-[#f7f9fc] border border-[#e5eaf2] min-w-0">
            <div class="min-w-0 flex-1">
              <div class="text-sm font-medium text-[#1b325a] break-words">
                {{ plan.name }}
              </div>

              <div v-if="plan.duration_days" class="text-xs text-[#71809b] mt-1">
                {{ plan.duration_days }} روز
              </div>

              <div class="text-xs text-[#71809b] mt-1">
                ترتیب نمایش: {{ plan.sort_order }}
              </div>

              <div v-if="!plan.has_region && plan.amount !== null" class="text-xs text-[#71809b] mt-1 break-words">
                {{ plan.amount }} {{ plan.currency }}
              </div>

              <div v-if="plan.has_region" class="mt-3 flex flex-col gap-2">
                <div v-for="region in plan.regions" :key="region.region"
                  class="flex flex-wrap items-center gap-2 text-xs text-[#71809b]">
                  <span class="font-medium text-[#1b325a]">
                    {{regionOptions.find(item => item.value === region.region)?.label}}
                  </span>

                  <span>—</span>

                  <span>
                    {{ region.amount }}
                    {{currencies.find(item => item.value === region.currency)?.label}}
                  </span>
                </div>
              </div>
            </div>

            <button type="button" class="shrink-0 text-[#ed145b] cursor-pointer text-xl" @click="removePlan(index)">
              ×
            </button>
          </div>
        </div>
      </div>

      <!-- Custom Fields (currently disabled) -->

    </form>
  </div>
</template>

<style scope>
.ql-toolbar.ql-snow {
  background-color: white;
  border-color: #eaeef5;
  border-top-right-radius: 10px;
  border-top-left-radius: 10px;
}

.ql-toolbar {
  direction: ltr !important;
  text-align: left !important;
}


.ql-editor {
  direction: rtl;
  text-align: right;
  font-family: inherit;
  min-height: 100px;
}

.ql-container.ql-snow {
  border: none
}
</style>